import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Globe } from "lucide-react";
import { supportedLngs } from "../i18n";

const languageNames = {
  en: "English",
  es: "Español",
  de: "Deutsch",
  ja: "日本語",
  fr: "Français",
  pt: "Português",
  ru: "Русский",
  it: "Italiano",
  nl: "Nederlands",
  pl: "Polski",
};

export default function LanguageSwitcher({ mobile = false }) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useParams();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const currentLang = lang && supportedLngs.includes(lang) ? lang : "en";

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const switchLanguage = (newLang) => {
    const pathWithoutLang = location.pathname.replace(
      new RegExp(`^/${currentLang}(/|$)`),
      "/"
    );
    const cleanPath = pathWithoutLang === "/" ? "" : pathWithoutLang;
    navigate(`/${newLang}${cleanPath}`);
    i18n.changeLanguage(newLang);
    setOpen(false);
  };

  if (mobile) {
    return (
      <div className="space-y-1">
        <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
          <Globe className="w-4 h-4" />
          Language
        </div>
        <div className="grid grid-cols-2 gap-1 px-2">
          {supportedLngs.map((lng) => (
            <button
              key={lng}
              onClick={() => switchLanguage(lng)}
              className={`text-left px-3 py-2 text-sm rounded-lg transition-colors ${
                lng === currentLang
                  ? "bg-[#62329f] text-white font-semibold"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              {languageNames[lng]}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 hover:text-[#62329f] transition-colors rounded-lg hover:bg-gray-50"
        aria-label="Switch language"
      >
        <Globe className="w-4 h-4" />
        <span className="uppercase">{currentLang}</span>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-44 bg-white rounded-xl border border-gray-200 shadow-xl py-2 z-50">
          {supportedLngs.map((lng) => (
            <button
              key={lng}
              onClick={() => switchLanguage(lng)}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                lng === currentLang
                  ? "bg-purple-50 text-[#62329f] font-semibold"
                  : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              {languageNames[lng]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
